/**
 * DEFINICE BAREV SPOLEČNÁ PRO VŠECHNY BALÍČKY.
 * 
 * soubor je manuálně synchronizovaán pomocí ./syncColors.js v /
 */


/** Jednotně definovaná barva */
export type ColorDef = {
    r: number;
    g: number;
    b: number;
}

type ColorList<K extends string> = Record<K, ColorDef>;

/** Paleta dostupná uživatelům pro jejich grafiku */
const palettePublic = {
    red: { r: 255, g: 0, b: 0 },
    green: { r: 0, g: 255, b: 0 },
    blue: { r: 0, g: 0, b: 255 },
    yellow: { r: 255, g: 255, b: 0 },
    cyan: { r: 0, g: 255, b: 255 },
    magenta: { r: 255, g: 0, b: 255 },
    teal: { r: 0, g: 150, b: 136 },
    orange: { r: 255, g: 165, b: 0 },
    purple: { r: 156, g: 39, b: 176 },
    pink: { r: 233, g: 67, b: 99 },
    "light-green": { r: 139, g: 195, b: 74 },
    "light-blue": { r: 33, g: 150, b: 244 },
} as const;

/** Klíče barev dostupných veřejně v animaci */
export type PublicColorKey = keyof typeof palettePublic;

/** Další pomocné barvy */
const paletteInternal = {
    white: { r: 255, g: 255, b: 255 },
    black: { r: 0, g: 0, b: 0 },
} as const;

/** Klíče barev dostupných interně pro pomocné účely */
export type InternalColorKey = keyof typeof paletteInternal;

const availableColors = {
    ...palettePublic,
    ...paletteInternal
} as const;

/** Klíče všech dostupných barev */
export type AvailableColor = keyof typeof availableColors;

class ColorPalette<K extends string> {

    public constructor(
        private readonly _palette: ColorList<K>
    ) {}

    /** Převeď celou paletu do formátu pro shader - hodnoty od 0 do 1 */
    mapToShaderFormat(): ColorList<K> {

        const entries = Object.entries( this._palette ) as [K, ColorDef][];

        const mapped = entries.map( entry => {

                entry[1].r /= 256;
                entry[1].g /= 256;
                entry[1].b /= 256;

                return entry;

        } );

        return Object.fromEntries(mapped) as ColorList<K>;

    }

    /** Převeď celou paletu do formátu pro CSS - hodnoty vyjádřené v RGB stringu */
    mapToCssFormat(): { [key in K]: string } {

        const entries = Object.entries( this._palette ) as [K, ColorDef][];

        const mapped = entries.map( ([key, def]) => [key, `rgb(${def.r}, ${def.g}, ${def.b})`] );

        return Object.fromEntries( mapped ) as { [key in K]: string };

    }

}


/** Sdílená definice barevné palety napříč balíčky */
export class ColorPalettes {

    private static _public: ColorPalette<PublicColorKey> = new ColorPalette( palettePublic );
    private static _internal: ColorPalette<InternalColorKey> = new ColorPalette( paletteInternal );
    private static _all: ColorPalette<AvailableColor> = new ColorPalette( availableColors );

    /** Barvy určené pro veřejné použití v animaci */
    public static get public() { return this._public; }

    /** Barvy určené pro interní použití */
    public static get internal() { return this._internal; }

    /** Všechny dostupné barvy */
    public static get all() { return this._all; }

    /** Zamezíme tvoření instancí */
    private constructor() {}

    
}